import type {
  FundraiseStatus,
  LaunchpadIdoSale,
  LaunchpadOffering,
} from '../models/launchpad.types';

export function resolveFundraiseStatus(
  startIso: string,
  endIso: string,
  now: Date = new Date(),
): FundraiseStatus {
  const start = Date.parse(startIso);
  const end = Date.parse(endIso);
  const ts = now.getTime();

  if (Number.isNaN(start) || Number.isNaN(end)) {
    return 'cancelled';
  }
  if (ts < start) {
    return 'upcoming';
  }
  if (ts > end) {
    return 'ended';
  }
  return 'live';
}

/** Prefer on-chain IdoSale window for listing/invest status when a sale is attached. */
export function withIdoSaleSchedule(
  offering: LaunchpadOffering,
  sale: LaunchpadIdoSale | null,
  now: Date = new Date(),
): LaunchpadOffering {
  if (!sale) {
    return { ...offering, idoSale: null };
  }
  return {
    ...offering,
    idoSale: sale,
    fundraiseStatus: resolveFundraiseStatus(sale.saleStart, sale.saleEnd, now),
    fundraisingStartTime: sale.saleStart,
    fundraisingEndTime: sale.saleEnd,
  };
}

/** Calendar-day compare for project form dates vs IdoSale schedule. */
export function fundraiseScheduleMismatch(
  projectStartIso: string | null | undefined,
  projectEndIso: string | null | undefined,
  saleStartIso: string,
  saleEndIso: string,
): boolean {
  const projectStart = toUtcDateKey(projectStartIso);
  const projectEnd = toUtcDateKey(projectEndIso);
  const saleStart = toUtcDateKey(saleStartIso);
  const saleEnd = toUtcDateKey(saleEndIso);
  if (!projectStart || !projectEnd || !saleStart || !saleEnd) {
    return false;
  }
  return projectStart !== saleStart || projectEnd !== saleEnd;
}

function toUtcDateKey(iso: string | null | undefined): string | null {
  if (!iso) {
    return null;
  }
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) {
    return null;
  }
  return new Date(ms).toISOString().slice(0, 10);
}

export function fundraiseProgressPercent(offering: LaunchpadOffering): number {
  if (offering.fundraisingGoal <= 0) {
    return 0;
  }
  const pct = (offering.totalRaised / offering.fundraisingGoal) * 100;
  return Math.min(100, Math.max(0, Math.round(pct)));
}

export function fundraiseStatusLabel(status: FundraiseStatus): string {
  switch (status) {
    case 'upcoming':
      return 'Upcoming';
    case 'live':
      return 'Live';
    case 'ended':
      return 'Ended';
    case 'cancelled':
      return 'Unavailable';
    default:
      return status;
  }
}
